/**
 * VerificationQueuePage — admin screen for the tutor verification queue.
 *
 * - Secondary filter tabs (En attente / Vérifiés / Suspendus)
 * - Left: TutorList filtered by active tab
 * - Right: TutorProfileView + VerificationActions for the selected tutor
 * - Actions call POST /tutors/{id}/verify or /reject; list refreshes; Toast fires; panel closes
 */

import { useState, useEffect } from 'react';
import { useTutorQueue } from '../../hooks/useTutorQueue';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/apiService';
import StatusTabs from '../../components/verification/StatusTabs';
import TutorList from '../../components/verification/TutorList';
import TutorProfileView from '../../components/verification/TutorProfileView';
import VerificationActions from '../../components/verification/VerificationActions';

const VerificationQueuePage = () => {
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState('pending_verification');
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [fullProfile, setFullProfile] = useState(null);

  const { tutors, allTutors, loading, error, refresh } = useTutorQueue(activeTab);

  // Build tab counts from allTutors
  const counts = {
    pending_verification: allTutors.filter((t) => t.status === 'pending_verification').length,
    verified: allTutors.filter((t) => t.status === 'verified').length,
    suspended: allTutors.filter((t) => t.status === 'suspended').length,
  };

  // Load full profile when a tutor is selected
  useEffect(() => {
    if (!selectedTutor) {
      setFullProfile(null);
      return;
    }

    let cancelled = false;
    setProfileLoading(true);
    setFullProfile(null);

    apiService
      .getTutor(selectedTutor.id)
      .then((res) => {
        if (!cancelled) {
          const data = res?.data ?? res;
          setFullProfile(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFullProfile(selectedTutor); // fallback to list data
        }
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedTutor]);

  const handleSelect = (tutor) => {
    setSelectedTutor(tutor);
  };

  const handleTabChange = (status) => {
    setActiveTab(status);
    setSelectedTutor(null);
    setFullProfile(null);
  };

  const handleApprove = async () => {
    await apiService.verifyTutor(selectedTutor.id);
    toast('Répétiteur approuvé avec succès.', 'success');
    setSelectedTutor(null);
    setFullProfile(null);
    refresh();
  };

  const handleReject = async (reason) => {
    await apiService.rejectTutor(selectedTutor.id, { rejection_reason: reason });
    toast('Répétiteur rejeté.', 'info');
    setSelectedTutor(null);
    setFullProfile(null);
    refresh();
  };

  const handleSuspend = async (reason) => {
    await apiService.suspendTutor(selectedTutor.id, { rejection_reason: reason });
    toast('Répétiteur suspendu.', 'info');
    setSelectedTutor(null);
    setFullProfile(null);
    refresh();
  };

  return (
    <div className="min-h-full">
      <div className="mb-6">
        <h1 className="text-2xl font-playfair font-bold text-[#17386E]">
          File de vérification
        </h1>
        <p className="text-sm text-gray-500 font-montserrat mt-1">
          Gérez les demandes d'inscription des répétiteurs.
        </p>
      </div>

      <StatusTabs active={activeTab} counts={counts} onChange={handleTabChange} />

      <div className="flex gap-6">
        {/* List panel */}
        <div className="flex-1 min-w-0">
          <TutorList
            tutors={tutors}
            selectedId={selectedTutor?.id}
            onSelect={handleSelect}
            loading={loading}
            error={error}
          />
        </div>

        {/* Profile panel */}
        {selectedTutor && (
          <div className="w-96 shrink-0 bg-white rounded-xl border border-gray-200 p-5 max-h-[calc(100vh-13rem)] overflow-y-auto">
            <TutorProfileView tutor={fullProfile} loading={profileLoading} />
            {!profileLoading && fullProfile && (
              <VerificationActions
                tutor={fullProfile}
                onApprove={handleApprove}
                onReject={
                  activeTab === 'verified' ? handleSuspend : handleReject
                }
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VerificationQueuePage;
